import React, {Component, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {changeProperty, changeScript} from "store/actions/asset";
import {ACTION_TYPE} from "utils/constants";
import View from "./View";
import AutoCompleter from "./AutoCompleter";
import * as aceManager from "ace-builds/src-noconflict/ext-language_tools";

function Container(props) {
  const dispatch = useDispatch();
  const {selectedObjects, action, scenes} = useSelector(state => state.asset);
  const {currentScript} = useSelector(state => state.codeEditor);
  const versionCompatibility = useSelector(state => state.version.versionCompatibility);

  const userScript = currentScript.source;
  const minTextSize = 12;
  const maxTextSize = 24;
  const autoCompleter = new AutoCompleter(scenes, versionCompatibility.script.isOOP, versionCompatibility.avatar.isCreatedByDefault);
  const completers = {
    apiCompleter: autoCompleter.getApiCompleter(),
    objectCompleter: autoCompleter.getObjectCompleter(),
  };

  const [textSize, setTextSize] = useState(16);
  const [currentPath, setCurrentPath] = useState("");
  const [pendingUpdate, setPendingUpdate] = useState(undefined);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  useEffect(() => {
    setAutoCompleters();
  }, []);

  useEffect(() => {
    let path = "";
    let subject = scenes.default.getObjectByProperty("uuid", currentScript.objUuid);

    if (!subject) subject = scenes.GUI.getObjectByProperty("uuid", currentScript.objUuid);
    if (!subject) return;

    if (currentScript.type === "scriptJS") {
      path = "default";
      setCurrentPath(path);
      return;
    }

    const scriptTitle = currentScript.title ? currentScript.title : "Untitled";
    if (subject !== undefined) {
      const objectTitle = subject.title;

      while (subject.parent) {
        path = subject.parent.title + "/" + path;
        subject = subject.parent;
      }

      path = path + objectTitle + "/" + scriptTitle;
      setCurrentPath(path);
    } else {
      // Need more development for GUI script
      path = "default" + "/" + scriptTitle;
      setCurrentPath(path);
    }
  }, [currentScript]);

  useEffect(() => {
    if (versionCompatibility.script.isOOP) {
      if (
        action.command === ACTION_TYPE.ADD_OBJECT ||
        action.command === ACTION_TYPE.DELETE_OBJECT ||
        (action.command === ACTION_TYPE.CHANGE_PROPERTY && action.category === "title")
      ) {
        if (pendingUpdate) clearTimeout(pendingUpdate);
        setPendingUpdate(
          setTimeout(() => {
            updateAutoCompleters();
            setPendingUpdate(undefined);
          }, 1500),
        );
      }
    } else {
      if (
        action.command === ACTION_TYPE.DELETE_OBJECT ||
        (action.command === ACTION_TYPE.CHANGE_PROPERTY && (action.category === "script" || action.category === "title"))
      ) {
        if (pendingUpdate) clearTimeout(pendingUpdate);
        setPendingUpdate(
          setTimeout(() => {
            updateAutoCompleters();
            setPendingUpdate(undefined);
          }, 1500),
        );
      }
    }
  }, [action]);

  const createAction = ({category, name, value}) => {
    return {
      command: ACTION_TYPE.CHANGE_PROPERTY,
      category,
      name,
      value,
    };
  };

  const setAutoCompleters = () => {
    const completerList = Object.keys(completers).map(type => completers[type]);
    aceManager.setCompleters([aceManager.textCompleter, ...completerList]);
  };

  const updateAutoCompleters = () => {
    completers.objectCompleter = autoCompleter.updateObjectCompleter();
    setAutoCompleters();
  };

  const onChangeScript = (value, event) => {
    if (currentScript.type === "serverScript") {
      dispatch(
        changeProperty(
          createAction({
            category: "script",
            name: "editServerScript",
            value: {index: currentScript.index, source: value},
          }),
        ),
      );
    } else if (currentScript.type === "clientScript") {
      dispatch(
        changeProperty(
          createAction({
            category: "script",
            name: "editClientScript",
            value: {index: currentScript.index, source: value},
          }),
        ),
      );
    } else if (currentScript.type === "scriptJS") {
      dispatch(changeScript(value, null));
    }
  };

  const onClickZoomIn = () => {
    if (textSize >= maxTextSize) return;
    setTextSize(textSize + 2);
  };

  const onClickZoomOut = () => {
    if (textSize <= minTextSize) return;
    setTextSize(textSize - 2);
  };

  const onClickCodeDelete = () => {
    setIsDeletePopupOpen(!isDeletePopupOpen);
  };

  const onClickDeleteConfirm = value => {
    if (value === true) window.ace.edit("ace-editor").setValue("");
    setIsDeletePopupOpen(false);
  };

  return (
    <View
      userScript={userScript}
      onChangeScript={onChangeScript}
      textSize={textSize}
      canZoomIn={textSize < maxTextSize}
      canZoomOut={textSize > minTextSize}
      onClickZoomIn={onClickZoomIn}
      onClickZoomOut={onClickZoomOut}
      isDeletePopupOpen={isDeletePopupOpen}
      onClickCodeDelete={onClickCodeDelete}
      onClickDeleteConfirm={onClickDeleteConfirm}
      currentPath={currentPath}
    />
  );
}

export default Container;
